// Utility functions for table operations

/**
 * Creates a filter change handler function
 * @param {Function} setFilters - State setter for filters
 * @returns {Function} Handler function for filter changes
 */
export const createFilterChangeHandler = (setFilters) => {
  return (field) => (e) => {
    setFilters(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };
};

/**
 * Clears all filters
 * @param {Function} setFilters - State setter for filters
 * @param {Object} initialFilters - Initial filter state
 */
export const clearFilters = (setFilters, initialFilters) => {
  setFilters(initialFilters);
};

/**
 * Filters data based on filter criteria
 * @param {Array} data - Data to filter
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered data
 */
export const filterData = (data, filters) => {
  if (!Array.isArray(data)) return [];
  
  return data.filter(item => {
    return Object.entries(filters).every(([key, value]) => {
      if (!value || value === '') return true;
      
      const itemValue = item[key];
      
      // Handle boolean values
      if (typeof itemValue === 'boolean') {
        if (value === 'true') return itemValue === true;
        if (value === 'false') return itemValue === false;
        return true;
      }
      
      // Handle string values
      if (typeof itemValue === 'string') {
        return itemValue.toLowerCase().includes(value.toLowerCase());
      }
      
      // Handle number values
      if (typeof itemValue === 'number') {
        return itemValue.toString().includes(value.toString());
      }
      
      // Handle exact matches
      return itemValue === value;
    });
  });
};

/**
 * Creates DataGrid columns with consistent styling
 * @param {Array} columns - Column definitions
 * @returns {Array} Styled column definitions
 */
export const createStyledColumns = (columns) => {
  return columns.map(col => ({
    ...col,
    headerClassName: 'data-grid-header',
    cellClassName: 'data-grid-cell',
    flex: col.flex || 1,
    minWidth: col.minWidth || 100,
  }));
};

/**
 * Common DataGrid styling
 */
export const dataGridStyles = {
  border: 'none',
  '& .MuiDataGrid-row': {
    '&:nth-of-type(even)': {
      backgroundColor: 'grey.50'
    },
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderColor: 'divider'
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'background.paper',
    borderBottom: '2px solid',
    borderColor: 'divider'
  },
  '& .MuiDataGrid-columnHeader': {
    fontWeight: 600
  }
};

/**
 * Common DataGrid props
 */
export const dataGridProps = {
  pagination: true,
  pageSizeOptions: [10, 25, 50, 100],
  rowsPerPageOptions: [10, 25, 50, 100],
  autoHeight: true,
  disableRowSelectionOnClick: true,
  sx: dataGridStyles
};

/**
 * Creates a status chip component
 * @param {string} status - Status value
 * @param {Object} statusConfig - Status configuration
 * @returns {Object} Chip component props
 */
export const createStatusChip = (status, statusConfig) => {
  const config = statusConfig[status] || statusConfig.default || {};
  return {
    label: config.label || status,
    color: config.color || 'default',
    size: 'small',
    sx: {
      fontWeight: 600,
      textTransform: 'uppercase',
      fontSize: '0.75rem'
    }
  };
};

/**
 * Common status configurations
 */
export const statusConfigs = {
  default: {
    label: 'Unknown',
    color: 'default'
  },
  active: {
    label: 'Active',
    color: 'success'
  },
  inactive: {
    label: 'Inactive',
    color: 'default'
  },
  pending: {
    label: 'Pending',
    color: 'warning'
  },
  completed: {
    label: 'Completed',
    color: 'success'
  },
  draft: {
    label: 'Draft',
    color: 'default'
  },
  published: {
    label: 'Published',
    color: 'success'
  },
  archived: {
    label: 'Archived',
    color: 'default'
  }
}; 